import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Package, Users, BarChart3, LogOut, Calculator, Wallet, Truck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { SessionExpiredModal } from '@/components/SessionExpiredModal';
import { dialogs } from '@/stores/dialog-store';

const topNavigation = [
  { name: 'Ventas', href: '/pos', icon: ShoppingCart, permissions: ['create_sales'] },
  { name: 'Catálogo', href: '/products', icon: Package, permissions: ['read_products'] },
  { name: 'Traspasos', href: '/inventory/transfers', icon: Truck, permissions: ['read_products'] },
  { name: 'Clientes', href: '/customers', icon: Users, permissions: ['read_customers'] },
  { name: 'Caja', href: '/cash-register', icon: Wallet, permissions: ['read_cash_register'] },
  { name: 'Facturación', href: '/invoices', icon: Calculator, permissions: ['read_invoicing'] },
  { name: 'Reportes', href: '/reports', icon: BarChart3, permissions: ['read_reports'] },
];

export function TopNavLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const userRole = user?.role || '';
  const userPermissions = user?.permissions || [];

  const handleLogout = async () => {
    if (!(await dialogs.confirm('¿Estás seguro de cerrar sesión?', { title: 'Cerrar Sesión', confirmText: 'Sí, Cerrar Sesión', cancelText: 'Cancelar' }))) return;
    logout();
  };

  const hasPermission = (perms: string[]) => {
    if (userRole === 'SUPER_ADMIN') return true;
    if (perms.length === 0) return true;
    return perms.some((p) => userPermissions.includes(p));
  };

  const navigation = topNavigation.filter(item => hasPermission(item.permissions));

  const isActive = (href: string) => {
    if (href === '/pos' && (location.pathname === '/' || location.pathname === '/pos')) return true;
    return location.pathname.startsWith(href);
  };

  const isPosPage = location.pathname === '/' || location.pathname === '/pos';

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-ios-background">
      {/* Barra de Navegación Superior Estilo Eleventa */}
      <header className="bg-white border-b border-ios-gray-5 shadow-sm z-50 flex-shrink-0">
        <div className="flex items-center justify-between px-4 py-2 max-w-[1600px] mx-auto w-full">
          
          {/* Menú de Botones */}
          <nav className="flex items-center gap-2 overflow-x-auto">
            {navigation.map((item) => {
              const active = isActive(item.href);
              return (
                <button
                  key={item.name}
                  onClick={() => navigate(item.href)}
                  className={cn(
                    'flex flex-col items-center justify-center gap-1 min-w-[80px] h-[64px] px-3 rounded-[12px]',
                    'transition-all duration-200 ease-in-out',
                    active 
                      ? 'bg-ios-blue text-white shadow-md shadow-ios-blue/30 scale-105' 
                      : 'text-ios-gray hover:bg-ios-gray-6 hover:text-ios-dark'
                  )}
                >
                  <item.icon size={24} strokeWidth={active ? 2.5 : 2} />
                  <span className="text-[11px] font-semibold">{item.name}</span>
                </button>
              );
            })}
          </nav>

          {/* Perfil y Salida */}
          {!isPosPage && (
            <div className="flex items-center gap-4 ml-4 pl-4 border-l border-ios-gray-5">
              <div className="hidden sm:flex flex-col items-end">
                <span className="text-sm font-bold text-ios-dark">{user?.fullName}</span>
                <span className="text-[11px] font-semibold text-ios-blue bg-ios-blue/10 px-2 py-0.5 rounded-full mt-0.5 uppercase tracking-wide">
                  {user?.role}
                </span>
              </div>
              <button
                onClick={handleLogout}
                className="flex flex-col items-center justify-center gap-1 min-w-[70px] h-[64px] px-2 rounded-[12px] text-ios-red hover:bg-red-50 transition-colors"
              >
                <LogOut size={24} />
                <span className="text-[11px] font-semibold">Salir</span>
              </button>
            </div>
          )}
        </div>
      </header>

      <SessionExpiredModal />

      {/* Área Principal de Contenido */}
      <main className="flex-1 overflow-y-auto bg-[#F5F5F7]">
        <div className="h-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
